import { createContext, useContext, useEffect, useRef } from "react";

// Lets each settings section (profile, two-step login, communication
// preferences) tell SettingsDialog whether it has unsaved edits and how to
// save them, so closing the dialog or switching tabs can offer
// "save / discard" instead of silently dropping the changes.
export const UnsavedChangesContext = createContext(null);

// Returns a registry the dialog owns: { set, remove, dirtyEntries }.
export function createUnsavedRegistry() {
  const entries = new Map();
  return {
    set(key, entry) {
      entries.set(key, entry);
    },
    remove(key) {
      entries.delete(key);
    },
    dirtyEntries() {
      return [...entries.values()].filter((e) => e.dirty);
    },
  };
}

// `save` must resolve to true on success and false on failure (the section
// shows its own error message either way).
export function useUnsavedChanges(key, dirty, save) {
  const registry = useContext(UnsavedChangesContext);
  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    if (!registry) return undefined;
    registry.set(key, { dirty, save: () => saveRef.current() });
    return undefined;
  }, [registry, key, dirty]);

  useEffect(() => {
    if (!registry) return undefined;
    return () => registry.remove(key);
  }, [registry, key]);
}
